/**
 * ValueReadout.ts
 *
 * Provides real-time cursor hover tooltips for weather layers.
 * For atmospheric and surface temperature layers (Current RTMA, Forecast HRTEMP, Wind Chill),
 * samples raw raster tile pixels directly to achieve exact unblended temperature readings
 * unaffected by underlying basemaps, labels, or hillshade.
 */

import maplibregl from 'maplibre-gl';
import store from '../state';
import LayerManager from '../layers/LayerManager';
import TilePixelSampler, {
  invertTempColor,
  invertFreezingLevelColor,
  invertAqiColor,
  invertAqhiColor,
  invertBlend50Color,
  invertDarkSkiesColor,
  invertRhColor,
  invertDewpointColor,
  invertFogColor,
  invertOpticsDewRiskColor,
  invertSeeingColor,
  invertTurbulenceColor,
  invertCapeColor,
  invertThunderstormColor,
  invertUvColor,
  invertSstColor,
  invertPrecipAccumColor,
  invertSnowAccumColor,
  invertSnowDepthColor,
  invertLenticularColor,
  invertMushroomColor,
  invertFoliageColor,
  invertCloudsColor
} from '../services/TilePixelSampler';
import { ClimateService } from '../services/ClimateService';
import {
  RECENT_CLIMATE_MONTH_FULL_NAMES,
  RecentClimateProbeResult,
  RecentClimateService
} from '../services/RecentClimateService';
import { getGoldenWindowRating, getDarkSkiesRating } from '../utils/goldenWindowRating';
import { WindsLayer } from '../layers/WindsLayer';

function getWindCompass(deg: number): string {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(deg / 22.5) % 16;
  return directions[index];
}

export class ValueReadout {
  private container: HTMLElement;
  private almanacContainer: HTMLElement;
  private isVisible: boolean = false;
  private isAlmanacVisible: boolean = false;
  private isDragging: boolean = false;
  private rafId: number | null = null;
  private lastEvent: maplibregl.MapMouseEvent | null = null;
  private queryCounter: number = 0;
  private tileSampler = TilePixelSampler.getInstance();

  constructor(
    private map: maplibregl.Map,
    private layerManager?: LayerManager
  ) {
    this.container = document.createElement('div');
    this.container.className = 'gw-value-readout-tooltip';
    this.container.style.cssText = `
      position: absolute;
      pointer-events: none;
      z-index: 9999;
      background: rgba(15, 23, 42, 0.90);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      color: #f8fafc;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.3px;
      padding: 5px 10px;
      border-radius: 7px;
      border: 1px solid rgba(255, 255, 255, 0.18);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.40);
      display: none;
      transform: translate(-50%, -135%);
      white-space: nowrap;
      transition: opacity 0.1s ease;
    `;

    this.almanacContainer = document.createElement('div');
    this.almanacContainer.className = 'gw-recent-climate-almanac-tooltip';
    this.almanacContainer.style.cssText = `
      position: absolute;
      pointer-events: none;
      z-index: 9999;
      background: rgba(11, 15, 24, 0.94);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.18);
      border-radius: 12px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.72);
      padding: 11px 13px;
      width: 288px;
      color: #f8fafc;
      font-family: system-ui, -apple-system, sans-serif;
      display: none;
      flex-direction: column;
      gap: 7px;
    `;

    const parent = map.getContainer();
    parent.appendChild(this.container);
    parent.appendChild(this.almanacContainer);

    this.map.on('mousemove', this.handleMouseMove);
    this.map.on('mouseleave', this.handleMouseLeave);
    this.map.on('movestart', this.handleDragStart);
    this.map.on('dragstart', this.handleDragStart);
    this.map.on('moveend', this.handleDragEnd);
    this.map.on('dragend', this.handleDragEnd);
  }

  public setLayerManager(lm: LayerManager): void {
    this.layerManager = lm;
  }

  private handleDragStart = () => {
    this.isDragging = true;
    this.hide();
  };

  private handleDragEnd = () => {
    this.isDragging = false;
  };

  private handleMouseMove = (e: maplibregl.MapMouseEvent) => {
    if (this.isDragging) {
      this.hide();
      return;
    }
    this.lastEvent = e;
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(this.processHover);
    }
  };

  private handleMouseLeave = () => {
    this.lastEvent = null;
    this.hide();
  };

  private processHover = async () => {
    this.rafId = null;
    const e = this.lastEvent;
    if (!e || this.isDragging) {
      this.hide();
      return;
    }

    const state = store.getState();
    const primary = state.primaryProduct;

    // Check if user disabled mouseover tooltips
    const isHoverEnabled = state.temperatureHoverReadoutEnabled !== false;
    if (!isHoverEnabled) {
      this.hide();
      return;
    }

    const lng = e.lngLat.lng;
    const lat = e.lngLat.lat;

    // 0. Surface Wind Layer Readout (Direct grid sampling)
    if (primary === 'winds' || (this.layerManager && this.layerManager.hasLayer('winds'))) {
      const windsInstance = WindsLayer.getInstance();
      if (windsInstance) {
        const wind = windsInstance.getWindAt(lng, lat);
        if (wind && wind.speed > 0.05) {
          let dirDeg = (270 - Math.atan2(wind.v, wind.u) * (180 / Math.PI)) % 360;
          if (dirDeg < 0) dirDeg += 360;
          const cardinal = getWindCompass(dirDeg);
          const isImperial = state.units !== 'metric';
          const speedVal = isImperial ? Math.round(wind.speed * 2.23694) : Math.round(wind.speed * 3.6);
          const gustVal = isImperial ? Math.round(wind.gust * 2.23694) : Math.round(wind.gust * 3.6);
          const unit = isImperial ? 'mph' : 'km/h';
          const gustText = gustVal > speedVal + 3 ? ` (Gusts ${gustVal} ${unit})` : '';
          this.container.innerText = `💨 Wind: ${cardinal} ${speedVal} ${unit}${gustText}`;
          this.container.style.left = `${e.point.x}px`;
          this.container.style.top = `${e.point.y}px`;
          this.show();
          return;
        }
      }
    }

    // 0.5. Recent Climate Data Almanac Card (Hybrid 4.6 km CONUS + 0.5° Global Land GWRC binary probe)
    const isRecentClimateActive =
      (this.layerManager && this.layerManager.hasLayer('recent_climate')) ||
      state.recentClimateEnabled ||
      primary === 'recent_climate' ||
      !!(state.activeLayerProducts && state.activeLayerProducts['recent_climate'] === true);

    if (isRecentClimateActive && state.manifest) {
      const sel = RecentClimateService.resolveSelection(
        state.manifest,
        state.recentClimateYear,
        state.recentClimateTimeMode,
        state.recentClimateMonth,
        state.recentClimateVariable
      );

      let probe = RecentClimateService.probePointSync(
        state.manifest,
        sel.year,
        sel.timeMode,
        sel.month,
        lat,
        lng
      );

      if (probe === undefined) {
        const queryId = ++this.queryCounter;
        probe = await RecentClimateService.probePoint(
          state.manifest,
          sel.year,
          sel.timeMode,
          sel.month,
          lat,
          lng
        );
        if (queryId !== this.queryCounter || this.isDragging || !this.lastEvent) {
          return;
        }
      }

      if (!probe || !probe.valid) {
        this.hide();
        return;
      }

      this.renderRecentClimateAlmanac(e, probe, state, sel);
      return;
    }

    // 1. Historical Climatology product readout
    const isClimateActive = (this.layerManager && this.layerManager.hasLayer('climate'))
      || state.climateEnabled
      || primary === 'climate'
      || (state.activeLayerProducts && (state.activeLayerProducts['climate'] === true || state.activeLayerProducts['climate_data'] === true));

    if (isClimateActive && this.layerManager && this.layerManager.hasLayer('climate')) {
      const activeTiles = this.layerManager.getActiveSourceTiles('climate');
      const tileUrl = activeTiles && activeTiles.length > 0 ? activeTiles[0] : null;

      if (tileUrl) {
        const queryId = ++this.queryCounter;
        const sampleZoom = Math.min(6, Math.max(0, Math.floor(this.map.getZoom())));
        const pixel = await this.tileSampler.samplePixel(tileUrl, lng, lat, sampleZoom);

        if (queryId !== this.queryCounter || this.isDragging || !this.lastEvent) {
          return;
        }

        if (pixel && pixel.a >= 25) {
          const readoutText = this.formatClimateReadout(pixel, state);
          if (readoutText) {
            this.container.innerText = readoutText;
            this.container.style.left = `${e.point.x}px`;
            this.container.style.top = `${e.point.y}px`;
            this.show();
            return;
          }
        }
        this.hide();
        return;
      }
    }

    // 2. Identify candidate raster layer across full product suite
    let targetLayerId: string | null = null;
    let targetProduct: string | null = null;

    const candidateLayers: Array<{ id: string; product: string }> = [
      { id: 'hrtemp_current', product: 'hrtemp' },
      { id: 'live-weather-temp-gradient', product: 'hrtemp' },
      { id: 'hrtemp', product: 'hrtemp' },
      { id: 'windchill', product: 'windchill' },
      { id: 'sunrise', product: 'sunrise' },
      { id: 'goldenwindow', product: 'sunrise' },
      { id: 'alpenglow', product: 'alpenglow' },
      { id: 'goldenhour', product: 'goldenhour' },
      { id: 'darkskies', product: 'darkskies' },
      { id: 'clouds', product: 'clouds' },
      { id: 'relative_humidity', product: 'relative_humidity' },
      { id: 'dewpoint', product: 'dewpoint' },
      { id: 'valley_fog', product: 'valley_fog' },
      { id: 'optics_dew_risk', product: 'optics_dew_risk' },
      { id: 'seeing', product: 'seeing' },
      { id: 'turbulence', product: 'turbulence' },
      { id: 'cape', product: 'cape' },
      { id: 'thunderstorm', product: 'thunderstorm' },
      { id: 'sun_intensity', product: 'sun_intensity' },
      { id: 'live-weather-sun-intensity', product: 'sun_intensity' },
      { id: 'sst', product: 'sst' },
      { id: 'precip_accum', product: 'precip_accum' },
      { id: 'snow_accum', product: 'snow_accum' },
      { id: 'snow_depth', product: 'snow_depth' },
      { id: 'lenticular', product: 'lenticular' },
      { id: 'mushrooms', product: 'mushrooms' },
      { id: 'foliage', product: 'foliage' },
      { id: 'freezing_level_fill', product: 'freezing_level' },
      { id: 'freezing_level', product: 'freezing_level' },
      { id: 'aqi', product: 'aqi' }
    ];

    if (this.layerManager) {
      if (primary) {
        const primaryStr = String(primary);
        const normalizedPrimary =
          (primaryStr === 'golden_hour' || primaryStr === 'sunrise_forecast' || primaryStr === 'goldenwindow')
            ? 'sunrise'
            : ((primaryStr === 'alpenglow_forecast' || primaryStr === 'goldenhour') ? 'alpenglow' : primaryStr);
        const match = candidateLayers.find(
          c => (c.product === primaryStr || c.product === normalizedPrimary || c.id === normalizedPrimary) && this.layerManager!.hasLayer(c.id)
        );
        if (match) {
          targetLayerId = match.id;
          targetProduct = match.product;
        }
      }
      if (!targetLayerId) {
        for (const candidate of candidateLayers) {
          if (this.layerManager.hasLayer(candidate.id)) {
            targetLayerId = candidate.id;
            targetProduct = candidate.product;
            break;
          }
        }
      }
    }

    if (targetLayerId && targetProduct && this.layerManager) {
      const activeTiles = this.layerManager.getActiveSourceTiles(targetLayerId);
      const tileUrl = activeTiles && activeTiles.length > 0 ? activeTiles[0] : null;

      if (tileUrl) {
        const queryId = ++this.queryCounter;
        const sampleZoom = Math.min(5, Math.max(0, Math.floor(this.map.getZoom())));
        const pixel = await this.tileSampler.samplePixel(tileUrl, lng, lat, sampleZoom);

        if (queryId !== this.queryCounter || this.isDragging || !this.lastEvent) {
          return;
        }

        if (pixel && pixel.a >= 10) {
          const text = this.formatProductReadout(targetProduct, pixel, state, tileUrl);
          if (text) {
            this.container.innerText = text;
            this.container.style.left = `${e.point.x}px`;
            this.container.style.top = `${e.point.y}px`;
            this.show();
            return;
          }
        }
      }
    }

    this.hide();
  };

  private formatProductReadout(
    product: string,
    pixel: { r: number; g: number; b: number; a: number },
    state: any,
    tileUrl: string = ''
  ): string | null {
    const { r, g, b, a } = pixel;
    const isImperial = state.units !== 'metric';

    switch (product) {
      case 'sunrise': {
        const score = invertBlend50Color(r, g, b);
        if (score === null) return null;
        const rating = getGoldenWindowRating(score);
        const isSunset = tileUrl.includes('_sunset') || tileUrl.includes('evening');
        return isSunset
          ? `🌇 Sunset: ${score} (${rating})`
          : `🌅 Sunrise: ${score} (${rating})`;
      }

      case 'alpenglow': {
        const score = invertBlend50Color(r, g, b);
        if (score === null) return null;
        const rating = getGoldenWindowRating(score);
        if (state.alpenglowMode === 'goldenhour' || tileUrl.includes('goldenhour') || tileUrl.includes('_gh_')) {
          return `🌇 Golden Hour: ${score} (${rating})`;
        }
        return `🏔️ Alpenglow: ${score} (${rating})`;
      }

      case 'goldenhour': {
        const score = invertBlend50Color(r, g, b);
        if (score === null) return null;
        const rating = getGoldenWindowRating(score);
        return `🌇 Golden Hour: ${score} (${rating})`;
      }

      case 'darkskies': {
        const score = invertDarkSkiesColor(r, g, b);
        if (score === null) return null;
        const rating = getDarkSkiesRating(score);
        return `✨ Dark Skies: ${score.toFixed(1)}/10 (${rating})`;
      }

      case 'hrtemp':
      case 'windchill': {
        const celsius = invertTempColor(r, g, b);
        if (celsius === null) return null;
        const tempVal = isImperial ? Math.round(celsius * 1.8 + 32) : Math.round(celsius);
        const unitSymbol = isImperial ? '°F' : '°C';
        const icon = product === 'windchill' ? '❄️' : '🌡️';
        const label = product === 'windchill' ? 'Wind Chill' : 'Temp';
        return `${icon} ${label}: ${tempVal}${unitSymbol}`;
      }

      case 'clouds': {
        const level = state.cloudLevel || 'total';
        const pct = invertCloudsColor(r, g, b, a, level);
        if (pct === null) return null;
        const levelLabel = level.charAt(0).toUpperCase() + level.slice(1);
        return `☁️ ${levelLabel} Clouds: ${pct}%`;
      }

      case 'relative_humidity': {
        const rh = invertRhColor(r, g, b);
        if (rh === null) return null;
        return `💧 Humidity: ${rh}%`;
      }

      case 'dewpoint': {
        const c = invertDewpointColor(r, g, b);
        if (c === null) return null;
        const tempVal = isImperial ? Math.round(c * 1.8 + 32) : Math.round(c);
        const unitSymbol = isImperial ? '°F' : '°C';
        return `🌡️ Dew Point: ${tempVal}${unitSymbol}`;
      }

      case 'valley_fog': {
        const pct = invertFogColor(r, g, b);
        if (pct === null) return null;
        return `🌫️ Valley Fog: ${pct}%`;
      }

      case 'optics_dew_risk': {
        const risk = invertOpticsDewRiskColor(r, g, b);
        if (risk === null) return null;
        let tier = 'Low';
        if (risk >= 70) tier = 'High';
        else if (risk >= 40) tier = 'Elevated';
        return `🔭 Optics Dew Risk: ${risk}/100 (${tier})`;
      }

      case 'seeing': {
        const score = invertSeeingColor(r, g, b);
        if (score === null) return null;
        let tier = 'Average';
        if (score >= 80) tier = 'Superb';
        else if (score >= 60) tier = 'Good';
        else if (score < 40) tier = 'Poor';
        return `🔭 Seeing: ${score}/100 (${tier})`;
      }

      case 'turbulence':
      case 'aviation_turbulence': {
        const edr = invertTurbulenceColor(r, g, b);
        if (edr === null) return null;
        let tier = 'Smooth';
        if (edr >= 0.70) tier = 'Extreme';
        else if (edr >= 0.45) tier = 'Severe';
        else if (edr >= 0.20) tier = 'Moderate';
        else if (edr >= 0.10) tier = 'Light';
        return `✈️ Turbulence: ${edr.toFixed(2)} EDR (${tier})`;
      }

      case 'cape': {
        const jkg = invertCapeColor(r, g, b);
        if (jkg === null) return null;
        return `⚡ CAPE: ${jkg} J/kg`;
      }

      case 'thunderstorm': {
        const prob = invertThunderstormColor(r, g, b);
        if (prob === null) return null;
        return `⚡ Thunderstorm Prob: ${prob}%`;
      }

      case 'sun_intensity': {
        const uv = invertUvColor(r, g, b);
        if (uv !== null) {
          let tier = 'Low';
          if (uv >= 11) tier = 'Extreme';
          else if (uv >= 8) tier = 'Very High';
          else if (uv >= 6) tier = 'High';
          else if (uv >= 3) tier = 'Moderate';
          return `☀️ UV Index: ${uv} (${tier})`;
        }
        const approxFlux = Math.round(((r * 1.5 + g * 0.8) / 255) * 1100);
        return `☀️ Sun Intensity: ~${approxFlux} W/m²`;
      }

      case 'sst': {
        const f = invertSstColor(r, g, b);
        if (f === null) return null;
        const tempVal = isImperial ? Math.round(f) : Math.round(((f - 32) * 5) / 9);
        const unitSymbol = isImperial ? '°F' : '°C';
        return `🌊 Sea Temp: ${tempVal}${unitSymbol}`;
      }

      case 'precip_accum': {
        const inches = invertPrecipAccumColor(r, g, b);
        if (inches === null) return null;
        const valStr = isImperial ? `${inches.toFixed(2)}"` : `${(inches * 25.4).toFixed(1)} mm`;
        return `🌧️ Liquid Precip: ${valStr}`;
      }

      case 'snow_accum': {
        const inches = invertSnowAccumColor(r, g, b);
        if (inches === null) return null;
        const valStr = isImperial ? `${inches.toFixed(1)}"` : `${(inches * 2.54).toFixed(1)} cm`;
        return `❄️ Snow Accum: ${valStr}`;
      }

      case 'snow_depth': {
        const inches = invertSnowDepthColor(r, g, b);
        if (inches === null) return null;
        const valStr = isImperial ? `${Math.round(inches)}"` : `${Math.round(inches * 2.54)} cm`;
        return `❄️ Snow Depth: ${valStr}`;
      }

      case 'lenticular': {
        const pct = invertLenticularColor(r, g, b);
        if (pct === null) return null;
        return `🏔️ Lenticular Wave: ${pct}%`;
      }

      case 'mushrooms': {
        const score = invertMushroomColor(r, g, b);
        if (score === null) return null;
        let tier = 'Average';
        if (score >= 75) tier = 'Prime';
        else if (score >= 50) tier = 'Good';
        else if (score < 30) tier = 'Poor';
        return `🍄 Foraging Index: ${score}/100 (${tier})`;
      }

      case 'foliage': {
        const fol = invertFoliageColor(r, g, b);
        if (fol === null) return null;
        return `🍁 Foliage: ${fol.stage} (${fol.fpi}%)`;
      }

      case 'freezing_level': {
        const elevFt = invertFreezingLevelColor(r, g, b);
        if (elevFt === null) return null;
        let elevStr: string;
        if (elevFt <= 100) {
          elevStr = isImperial ? '0 ft (Surface)' : '0 m (Surface)';
        } else if (isImperial) {
          elevStr = `${Math.round(elevFt).toLocaleString()} ft`;
        } else {
          elevStr = `${Math.round(elevFt * 0.3048).toLocaleString()} m`;
        }
        return `🧊 Freezing Altitude: ${elevStr}`;
      }

      case 'aqi': {
        const isAqhiMode = state.selectedModels['aqi'] === 'raqdps' || state.aqiMode === 'aqhi';
        if (isAqhiMode) {
          const approxAqhi = invertAqhiColor(r, g, b);
          if (approxAqhi !== null) {
            let status = 'Low Risk';
            if (approxAqhi >= 11) status = 'Very High Risk';
            else if (approxAqhi >= 7) status = 'High Risk';
            else if (approxAqhi >= 4) status = 'Moderate Risk';
            return `🫁 AQHI: ${Math.round(approxAqhi)} (${status})`;
          }
        } else {
          const approxAqi = invertAqiColor(r, g, b);
          if (approxAqi !== null) {
            let status = 'Good';
            if (approxAqi > 300) status = 'Hazardous';
            else if (approxAqi > 200) status = 'Very Unhealthy';
            else if (approxAqi > 150) status = 'Unhealthy';
            else if (approxAqi > 100) status = 'Unhealthy for Sensitive Groups';
            else if (approxAqi > 50) status = 'Moderate';
            return `🫁 AQI: ${Math.round(approxAqi)} (${status})`;
          }
        }
        return null;
      }

      default:
        return null;
    }
  }

  private formatClimateReadout(
    pixel: { r: number; g: number; b: number; a: number },
    state: any
  ): string | null {
    const { r, g, b, a } = pixel;
    if (a < 25) return null;

    const cVar = state.climateSelectedVariable || 'tmax_mean';
    const cMode = state.climateViewMode || 'daily';
    const isImperial = state.units !== 'metric';
    const isMonthly = cMode === 'monthly' || cVar.endsWith('_monthly');

    // 1. Temperature fields (tmax_mean, tmax_p90, tmax_rec, tmin_mean, tmin_p90, tmin_rec)
    if (cVar.startsWith('tmax') || cVar.startsWith('tmin') || cVar.includes('temp')) {
      const fahrenheit = ClimateService.decodeTempPixel(r, g, b, a);
      if (fahrenheit === null) return null;
      const tempVal = isImperial ? Math.round(fahrenheit) : Math.round(((fahrenheit - 32) * 5) / 9);
      const unit = isImperial ? '°F' : '°C';
      let label = 'Temp';
      let icon = '🌡️';

      if (cVar === 'tmax_mean') label = 'Avg High';
      else if (cVar === 'tmax_p90') label = 'Warmest 10% High';
      else if (cVar === 'tmax_rec') label = 'Record High (1991–2020)';
      else if (cVar === 'tmin_mean') { label = 'Avg Low'; icon = '❄️'; }
      else if (cVar === 'tmin_p90') { label = 'Coldest 10% Low'; icon = '❄️'; }
      else if (cVar === 'tmin_rec') { label = 'Record Low (1991–2020)'; icon = '❄️'; }

      return `${icon} ${label}: ${tempVal}${unit}`;
    }

    // 2. Precipitation Probability
    if (cVar === 'precip_prob') {
      const pct = ClimateService.decodePrecipProbPixel(r, g, b, a);
      return `🌧️ Precip Prob: ${Math.round(pct)}%`;
    }

    // 3. Monthly Precipitation
    if (cVar === 'precip_monthly' || cVar === 'precip') {
      const inches = ClimateService.decodePrecipMonthlyPixel(r, g, b, a);
      if (isImperial) {
        return `🌧️ Monthly Precip: ${inches.toFixed(1)}"`;
      } else {
        return `🌧️ Monthly Precip: ${Math.round(inches * 25.4)} mm`;
      }
    }

    // 4. Snowfall, Snowpack & Snow Water Equivalent (SWE)
    if (cVar === 'snow_monthly' || cVar === 'snow' || cVar === 'snowpack' || cVar === 'snow_swe') {
      const inches = ClimateService.decodeSnowDepthPixel(r, g, b, a);
      if (cVar === 'snow_swe') {
        const valStr = isImperial ? `${inches.toFixed(1)}"` : `${Math.round(inches * 25.4)} mm`;
        return `💧 Snow Water Eq: ${valStr}`;
      } else if (cVar === 'snowpack') {
        const valStr = isImperial ? `${Math.round(inches)}"` : `${Math.round(inches * 2.54)} cm`;
        return `❄️ Snowpack Depth: ${valStr}`;
      } else {
        const valStr = isImperial ? `${inches.toFixed(1)}"` : `${Math.round(inches * 2.54)} cm`;
        return `❄️ Monthly Snow: ${valStr}`;
      }
    }

    // 5. Sunshine & Solar Energy
    if (cVar === 'solar_energy' || cVar === 'solar_energy_monthly') {
      const kwh = ClimateService.decodeSolarEnergyPixel(r, g, b, a);
      return `☀️ Solar Energy: ${kwh.toFixed(1)} kWh/m²`;
    }
    if (cVar === 'sunshine_pct' || cVar === 'sunshine_pct_monthly') {
      const pct = ClimateService.decodeSunshinePctPixel(r, g, b, a);
      return `☀️ Sunshine Exposure: ${Math.round(pct)}%`;
    }
    if (cVar === 'mostly_sunny' || cVar === 'mostly_sunny_monthly') {
      if (isMonthly) {
        const days = ClimateService.decodeDaysCountPixel(r, g, b, a);
        return `☀️ Mostly Sunny: ${Math.round(days)} d`;
      } else {
        const pct = ClimateService.decodeMostlySunnyPixel(r, g, b, a);
        return `☀️ Mostly Sunny: ${Math.round(pct)}%`;
      }
    }
    if (cVar === 'partly_cloudy' || cVar === 'partly_cloudy_monthly') {
      if (isMonthly) {
        const days = ClimateService.decodeDaysCountPixel(r, g, b, a);
        return `🌤️ Partly Cloudy: ${Math.round(days)} d`;
      } else {
        const pct = ClimateService.decodePartlyCloudyPixel(r, g, b, a);
        return `🌤️ Partly Cloudy: ${Math.round(pct)}%`;
      }
    }
    if (cVar === 'overcast' || cVar === 'overcast_monthly') {
      if (isMonthly) {
        const days = ClimateService.decodeDaysCountPixel(r, g, b, a);
        return `☁️ Overcast: ${Math.round(days)} d`;
      } else {
        const pct = ClimateService.decodeOvercastPixel(r, g, b, a);
        return `☁️ Overcast: ${Math.round(pct)}%`;
      }
    }

    // 6. Extreme Heat Days (Monthly)
    if (cVar.startsWith('days_gt_')) {
      const days = ClimateService.decodeDaysCountPixel(r, g, b, a, true);
      const thresh = cVar.replace('days_gt_', '');
      const dispThresh = isImperial ? `${thresh}°F` : `${Math.round(((parseInt(thresh, 10) - 32) * 5) / 9)}°C`;
      return `🔥 Days > ${dispThresh}: ${Math.round(days)} d`;
    }

    // 7. Extreme Cold Days (Monthly)
    if (cVar.startsWith('days_lt_')) {
      const days = ClimateService.decodeDaysCountPixel(r, g, b, a, false);
      const thresh = cVar.replace('days_lt_', '');
      const dispThresh = isImperial ? `${thresh}°F` : `${Math.round(((parseInt(thresh, 10) - 32) * 5) / 9)}°C`;
      return `🥶 Days < ${dispThresh}: ${Math.round(days)} d`;
    }

    // 8. Precipitation Days (Monthly)
    if (cVar === 'precip_days') {
      const days = ClimateService.decodeDaysCountPixel(r, g, b, a);
      return `🌧️ Precip Days: ${Math.round(days)} d`;
    }

    // 9. Wind Speed
    if (cVar === 'wind_speed' || cVar === 'wind') {
      const mph = ClimateService.decodeWindPixel(r, g, b, a);
      if (isImperial) {
        return `💨 Avg Wind: ${Math.round(mph)} mph`;
      } else {
        return `💨 Avg Wind: ${Math.round(mph * 1.60934)} km/h`;
      }
    }

    return null;
  }

  private renderRecentClimateAlmanac(
    e: maplibregl.MapMouseEvent,
    probe: RecentClimateProbeResult,
    state: any,
    sel: ReturnType<typeof RecentClimateService.resolveSelection>
  ): void {
    const isImperial = state.units !== 'metric';
    const spec = RecentClimateService.getMetricSpec(sel.variable);

    const periodTitle = sel.timeMode === 'month'
      ? `${RECENT_CLIMATE_MONTH_FULL_NAMES[sel.month - 1]} ${sel.year}`
      : (sel.yearMeta.is_complete_year ? `${sel.year} Annual` : `${sel.year} Year-to-Date`);

    const heroLabel = sel.timeMode === 'month'
      ? spec.monthTitle
      : (sel.yearMeta.is_complete_year ? spec.annualTitle : spec.ytdTitle);

    let heroVal = '--';
    if (sel.variable === 'tmax') heroVal = RecentClimateService.formatTemp(probe.tmaxF, isImperial, true);
    else if (sel.variable === 'tmin') heroVal = RecentClimateService.formatTemp(probe.tminF, isImperial, true);
    else if (sel.variable === 'tavg') heroVal = RecentClimateService.formatTemp(probe.tavgF, isImperial, true);
    else if (sel.variable === 'tanom') heroVal = RecentClimateService.formatTempAnomaly(probe.tanomF, isImperial);
    else if (sel.variable === 'prcp') heroVal = RecentClimateService.formatPrecip(probe.prcpIn, isImperial);
    else if (sel.variable === 'prcpanom') {
      const anomStr = RecentClimateService.formatPrecipAnomaly(probe.prcpanomIn, isImperial);
      const pctStr = probe.prcpPctNorm !== null ? ` (${probe.prcpPctNorm}%)` : '';
      heroVal = anomStr !== '--' ? `${anomStr}${pctStr}` : '--';
    } else if (sel.variable === 'snow') {
      heroVal = probe.source === 'conus_4km'
        ? RecentClimateService.formatSnow(probe.snowIn, isImperial)
        : 'CONUS only';
    }

    const highLowStr = `${RecentClimateService.formatTemp(probe.tmaxF, isImperial, false)} / ${RecentClimateService.formatTemp(probe.tminF, isImperial, true)}`;
    const meanStr = RecentClimateService.formatTemp(probe.tavgF, isImperial, true);

    let tanomHtml = '--';
    if (probe.tanomF !== null) {
      const clr = probe.tanomF > 0 ? '#f59e0b' : (probe.tanomF < 0 ? '#38bdf8' : '#e2e8f0');
      tanomHtml = `<span style="color:${clr};">${RecentClimateService.formatTempAnomaly(probe.tanomF, isImperial)} vs normal</span>`;
    }

    const prcpStr = RecentClimateService.formatPrecip(probe.prcpIn, isImperial);
    const pnormStr = RecentClimateService.formatPrecip(probe.prcpNormIn, isImperial);

    let prcpAnomHtml = '--';
    if (probe.prcpanomIn !== null) {
      const anomStr = RecentClimateService.formatPrecipAnomaly(probe.prcpanomIn, isImperial);
      const badgeColor = probe.prcpanomIn >= 0 ? '#10b981' : '#f59e0b';
      const pctHtml = probe.prcpPctNorm !== null
        ? ` • <span style="color:${badgeColor}; font-weight:700;">${probe.prcpPctNorm}% of normal</span>`
        : '';
      prcpAnomHtml = `${anomStr}${pctHtml}`;
    }

    const snowHtml = probe.source === 'conus_4km'
      ? RecentClimateService.formatSnow(probe.snowIn, isImperial)
      : '<span style="color:#94a3b8; font-weight:600;">CONUS only</span>';

    let elevM = probe.elevationM;
    if (elevM === null && typeof (this.map as any).queryTerrainElevation === 'function') {
      try {
        const demM = (this.map as any).queryTerrainElevation(e.lngLat);
        if (typeof demM === 'number' && !Number.isNaN(demM) && demM > -500 && demM < 9000) {
          elevM = Math.round(demM);
        }
      } catch (_) {}
    }

    let elevHtml = '--';
    if (elevM !== null) {
      const ft = Math.round(elevM * 3.28084);
      const m = Math.round(elevM);
      elevHtml = isImperial
        ? `${ft.toLocaleString()} ft <span style="opacity:0.75; font-weight:500;">(${m.toLocaleString()} m)</span>`
        : `${m.toLocaleString()} m <span style="opacity:0.75; font-weight:500;">(${ft.toLocaleString()} ft)</span>`;
    }

    const latLabel = `${Math.abs(probe.lat).toFixed(2)}°${probe.lat >= 0 ? 'N' : 'S'}`;
    const lonLabel = `${Math.abs(probe.lon).toFixed(2)}°${probe.lon >= 0 ? 'E' : 'W'}`;
    const resTag = probe.source === 'conus_4km' ? '4.6 km CONUS' : '0.5° Global';

    this.almanacContainer.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; gap:6px; border-bottom:1px solid rgba(255,255,255,0.10); padding-bottom:5px;">
        <div style="display:flex; flex-direction:column; gap:1px;">
          <span style="font-size:0.77rem; font-weight:800; color:#f8fafc;">${periodTitle} Climate</span>
          <span style="font-size:0.65rem; color:#94a3b8; font-family:ui-monospace, SFMono-Regular, Menlo, monospace;">${latLabel}, ${lonLabel}</span>
        </div>
        <span style="font-size:0.60rem; font-weight:700; color:#38bdf8; background:rgba(56,189,248,0.14); border:1px solid rgba(56,189,248,0.3); padding:2px 6px; border-radius:999px; white-space:nowrap;">${resTag}</span>
      </div>

      <div style="background:rgba(56,189,248,0.12); border:1px solid rgba(56,189,248,0.32); border-radius:8px; padding:6px 9px; display:flex; justify-content:space-between; align-items:center; gap:8px;">
        <span style="font-size:0.70rem; font-weight:700; color:#38bdf8;">${heroLabel}</span>
        <span style="font-size:1.05rem; font-weight:800; color:#ffffff; white-space:nowrap;">${heroVal}</span>
      </div>

      <div style="display:grid; grid-template-columns:1fr 1fr; gap:5px;">
        <div style="background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:6px; padding:4px 7px;">
          <div style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">High / Low</div>
          <div style="font-size:0.80rem; font-weight:700; color:#f8fafc;">${highLowStr}</div>
        </div>
        <div style="background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:6px; padding:4px 7px;">
          <div style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">Mean Temp</div>
          <div style="font-size:0.80rem; font-weight:700; color:#f8fafc;">${meanStr}</div>
        </div>

        <div style="grid-column:span 2; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:6px; padding:4px 7px; display:flex; justify-content:space-between; align-items:center;">
          <span style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">Temp Departure</span>
          <span style="font-size:0.78rem; font-weight:700;">${tanomHtml}</span>
        </div>

        <div style="background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:6px; padding:4px 7px;">
          <div style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">Precipitation</div>
          <div style="font-size:0.80rem; font-weight:700; color:#f8fafc;">${prcpStr}</div>
        </div>
        <div style="background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:6px; padding:4px 7px;">
          <div style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">1991–2020 Normal</div>
          <div style="font-size:0.80rem; font-weight:700; color:#f8fafc;">${pnormStr}</div>
        </div>

        <div style="grid-column:span 2; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:6px; padding:4px 7px; display:flex; justify-content:space-between; align-items:center;">
          <span style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">Precip Dept</span>
          <span style="font-size:0.77rem; font-weight:700; color:#f8fafc;">${prcpAnomHtml}</span>
        </div>

        <div style="background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:6px; padding:4px 7px;">
          <div style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">Snowfall</div>
          <div style="font-size:0.78rem; font-weight:700; color:#f8fafc;">${snowHtml}</div>
        </div>
        <div style="background:rgba(15,23,42,0.82); border:1px solid rgba(56,189,248,0.22); border-radius:6px; padding:4px 7px;">
          <div style="font-size:0.61rem; font-weight:600; text-transform:uppercase; color:#94a3b8;">Elevation</div>
          <div style="font-size:0.76rem; font-weight:700; color:#38bdf8;">${elevHtml}</div>
        </div>
      </div>
    `;

    const mapEl = this.map.getContainer();
    const mapW = mapEl.clientWidth || window.innerWidth;
    const mapH = mapEl.clientHeight || window.innerHeight;
    const cardW = 292;
    const cardH = 315;
    let left = e.point.x + 16;
    let top = e.point.y + 16;
    if (left + cardW + 10 > mapW) left = Math.max(8, e.point.x - cardW - 16);
    if (top + cardH + 10 > mapH) top = Math.max(8, e.point.y - cardH - 16);

    this.almanacContainer.style.left = `${left}px`;
    this.almanacContainer.style.top = `${top}px`;

    if (this.isVisible) {
      this.container.style.display = 'none';
      this.isVisible = false;
    }
    if (!this.isAlmanacVisible) {
      this.almanacContainer.style.display = 'flex';
      this.isAlmanacVisible = true;
    }
  }

  private show() {
    if (this.isAlmanacVisible) {
      this.almanacContainer.style.display = 'none';
      this.isAlmanacVisible = false;
    }
    if (!this.isVisible) {
      this.container.style.display = 'block';
      this.isVisible = true;
    }
  }

  private hide() {
    if (this.isVisible) {
      this.container.style.display = 'none';
      this.isVisible = false;
    }
    if (this.isAlmanacVisible) {
      this.almanacContainer.style.display = 'none';
      this.isAlmanacVisible = false;
    }
  }

  public destroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.map.off('mousemove', this.handleMouseMove);
    this.map.off('mouseleave', this.handleMouseLeave);
    this.map.off('movestart', this.handleDragStart);
    this.map.off('dragstart', this.handleDragStart);
    this.map.off('moveend', this.handleDragEnd);
    this.map.off('dragend', this.handleDragEnd);
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
    if (this.almanacContainer.parentElement) {
      this.almanacContainer.parentElement.removeChild(this.almanacContainer);
    }
  }
}

export default ValueReadout;
